import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Modal } from '@/components/ui/Modal'
import { useDevis, useDevisStats } from '@/hooks/useDevis'
import { formatCurrency, formatDate } from '@/lib/utils'
import { devisStatusLabels, DevisStatus } from '@/types/devis'
import type { Devis } from '@/types/devis'
import { QontoBalanceCard } from '@/components/admin/QontoBalanceCard'
import { QontoSyncBanner } from '@/components/admin/QontoSyncBanner'
import {
  Plus,
  Search,
  FileText,
  MoreHorizontal,
  Eye,
  Copy,
  FileOutput,
  Send,
  Trash2,
  TrendingUp,
  Clock,
  AlertCircle,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react'
import toast from 'react-hot-toast'

const statusFilters: { value: DevisStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'draft', label: 'Brouillons' },
  { value: 'sent', label: 'Envoyes' },
  { value: 'viewed', label: 'Vus' },
  { value: 'accepted', label: 'Acceptes' },
  { value: 'rejected', label: 'Refuses' },
  { value: 'expired', label: 'Expires' },
  { value: 'converted', label: 'Factures' },
]

export default function QuotesPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<DevisStatus | 'all'>('all')
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [devisToDelete, setDevisToDelete] = useState<Devis | null>(null)
  const [sortBy, setSortBy] = useState('date_emission')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const filters = useMemo(() => ({
    search: search || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [search, statusFilter])

  function handleSort(col: string) {
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortOrder('desc')
    }
  }

  function SortIcon({ col }: { col: string }) {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-mint-600" />
      : <ChevronDown className="w-3.5 h-3.5 text-mint-600" />
  }

  const {
    devisList,
    isLoading,
    deleteDevis,
    sendDevis,
    duplicateDevis,
    convertToFacture,
    isDeleting,
    isSending,
    isDuplicating,
    isConverting,
  } = useDevis(filters)

  const { data: stats, isLoading: statsLoading } = useDevisStats()

  const sortedDevis = useMemo(() => {
    if (!devisList.length) return devisList
    return [...devisList].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortBy]
      const bVal = (b as Record<string, unknown>)[sortBy]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }, [devisList, sortBy, sortOrder])

  const handleSend = async (devis: Devis) => {
    try {
      await sendDevis(devis.id)
      setShowActionsMenu(null)
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error)
    }
  }

  const handleDuplicate = async (devis: Devis) => {
    try {
      await duplicateDevis(devis.id)
      setShowActionsMenu(null)
    } catch (error) {
      console.error('Erreur lors de la duplication:', error)
    }
  }

  const handleConvert = async (devis: Devis) => {
    try {
      const result = await convertToFacture(devis.id)
      setShowActionsMenu(null)
      toast.success('Devis converti en facture avec succes')
      // Navigate to the new invoice
      navigate(`/invoices/${result.factureId}`)
    } catch (error) {
      console.error('Erreur lors de la conversion:', error)
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la conversion')
    }
  }

  const handleDelete = async () => {
    if (!devisToDelete) return
    try {
      await deleteDevis(devisToDelete.id)
      setShowDeleteModal(false)
      setDevisToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
    }
  }

  const confirmDelete = (devis: Devis) => {
    setDevisToDelete(devis)
    setShowDeleteModal(true)
    setShowActionsMenu(null)
  }

  const getStatusBadgeVariant = (status: DevisStatus): 'mint' | 'gold' | 'brown' | 'beige' | 'red' | 'default' => {
    switch (status) {
      case 'accepted':
        return 'mint'
      case 'sent':
      case 'viewed':
        return 'gold'
      case 'rejected':
        return 'red'
      case 'converted':
        return 'mint'
      default:
        return 'beige'
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Devis
          </h1>
          <p className="mt-1 text-brown-600">
            Creez et gerez vos devis clients
          </p>
        </div>
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/devis/new')}
        >
          Nouveau devis
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <QontoBalanceCard />

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-beige-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-beige-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Total devis</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.total || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gold-100 flex items-center justify-center">
            <Clock className="w-6 h-6 text-gold-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">En cours</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.en_cours || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-mint-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Taux conversion</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : `${stats?.taux_conversion || 0}%`}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">En attente</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : formatCurrency(stats?.montant_en_attente || 0)}
            </p>
          </div>
        </Card>
      </div>

      {/* Qonto sync */}
      <QontoSyncBanner type="quotes" />

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher par numero ou titre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-mint-600 text-white'
                    : 'bg-beige-100 text-brown-700 hover:bg-beige-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Devis list */}
      <Card padding="none" className="overflow-visible">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : devisList.length === 0 ? (
          <div className="text-center py-12 text-brown-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="font-medium">Aucun devis trouve</p>
            <p className="text-sm mt-1">
              {search || statusFilter !== 'all'
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Commencez par creer votre premier devis'}
            </p>
            {!search && statusFilter === 'all' && (
              <Button
                className="mt-4"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => navigate('/devis/new')}
              >
                Creer un devis
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  {([
                    { label: 'Numero / Client', col: 'numero', align: 'left' as const },
                    { label: 'Titre', col: 'titre', align: 'left' as const },
                    { label: 'Date', col: 'date_emission', align: 'left' as const },
                    { label: 'Statut', col: 'status', align: 'left' as const },
                    { label: 'Montant TTC', col: 'montant_ttc', align: 'right' as const },
                    { label: 'Actions', col: null, align: 'right' as const },
                  ]).map(({ label, col, align }) => (
                    <th
                      key={label}
                      className={`text-${align} px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider whitespace-nowrap ${col ? 'cursor-pointer select-none hover:text-brown-700' : ''}`}
                      onClick={col ? () => handleSort(col) : undefined}
                    >
                      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
                        {label}
                        {col && <SortIcon col={col} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {sortedDevis.map((devis) => (
                  <tr key={devis.id} className="hover:bg-beige-50 relative">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-gold-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-brown-900">
                            {devis.numero}
                          </div>
                          {devis.contact && (
                            <div className="text-sm text-brown-500">
                              {devis.contact.prenom} {devis.contact.nom}
                              {devis.contact.entreprise && ` - ${devis.contact.entreprise}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-brown-900 max-w-xs truncate">
                        {devis.titre || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-brown-600">
                        {formatDate(devis.date_emission)}
                      </div>
                      <div className="text-xs text-brown-400">
                        Expire le {formatDate(devis.date_validite)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Badge variant={getStatusBadgeVariant(devis.status)}>
                          {devisStatusLabels[devis.status]}
                        </Badge>
                        {devis.date_vue && devis.status !== 'draft' && (
                          <span className="text-xs text-purple-600 flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            Vu
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-brown-900">
                        {formatCurrency(devis.montant_ttc)}
                      </div>
                      <div className="text-xs text-brown-400">
                        HT: {formatCurrency(devis.montant_ht)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="relative">
                        <button
                          onClick={() => setShowActionsMenu(showActionsMenu === devis.id ? null : devis.id)}
                          className="text-brown-400 hover:text-brown-600 p-1 rounded-lg hover:bg-beige-100"
                        >
                          <MoreHorizontal className="w-5 h-5" />
                        </button>

                        {showActionsMenu === devis.id && (
                          <>
                            <div
                              className="fixed inset-0 z-[998]"
                              onClick={() => setShowActionsMenu(null)}
                            />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-beige-200 z-[999] py-1">
                              <button
                                onClick={() => {
                                  navigate(`/devis/${devis.id}`)
                                  setShowActionsMenu(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                Voir le devis
                              </button>

                              {devis.share_token && (
                                <a
                                  href={`/devis/view/${devis.share_token}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2"
                                  onClick={() => setShowActionsMenu(null)}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Lien client
                                </a>
                              )}

                              {devis.status === 'draft' && (
                                <button
                                  onClick={() => handleSend(devis)}
                                  disabled={isSending}
                                  className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <Send className="w-4 h-4" />
                                  Envoyer au client
                                </button>
                              )}

                              <button
                                onClick={() => handleDuplicate(devis)}
                                disabled={isDuplicating}
                                className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2 disabled:opacity-50"
                              >
                                <Copy className="w-4 h-4" />
                                Dupliquer
                              </button>

                              {devis.status === 'accepted' && (
                                <button
                                  onClick={() => handleConvert(devis)}
                                  disabled={isConverting}
                                  className="w-full px-4 py-2 text-left text-sm text-mint-700 hover:bg-mint-50 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <FileOutput className="w-4 h-4" />
                                  Convertir en facture
                                </button>
                              )}

                              {devis.status === 'draft' && (
                                <button
                                  onClick={() => confirmDelete(devis)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Supprimer
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setDevisToDelete(null)
        }}
        title="Supprimer le devis"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir supprimer le devis{' '}
            <span className="font-medium text-brown-900">
              {devisToDelete?.numero}
            </span>
            ? Cette action est irreversible.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false)
                setDevisToDelete(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeleting}
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
